'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { Loader2, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { KnowledgeSourcesPanel } from '@/components/knowledge-sources/knowledge-sources-panel'
import { KnowledgeBaseDocumentsPage } from '@/components/ai/knowledge-base'
import { useCleanAllKnowledgeBaseMutation } from '@/hooks/mutations/knowledge-base-clean-all'
import { useRadixBodyLockCleanup } from '@/hooks/use-radix-body-lock-cleanup'
import { useTranslations } from "next-intl"


export function KnowledgeBasePage() {
  const tUi = useTranslations("KnowledgeBase.KnowledgeBaseKnowledgeBasePage")
  const tCommonUi = useTranslations("Common")

  const router = useRouter()
  const [isCleanAllOpen, setIsCleanAllOpen] = useState(false)
  const [documentsRefreshKey, setDocumentsRefreshKey] = useState(0)
  const [sourceRefreshVersion, setSourceRefreshVersion] = useState(0)
  useRadixBodyLockCleanup(isCleanAllOpen)
  const cleanAllMutation = useCleanAllKnowledgeBaseMutation({
    onSuccess: (result) => {
      for (const warning of result.warnings) {
        toast.warning(warning)
      }
      setIsCleanAllOpen(false)
      setDocumentsRefreshKey((key) => key + 1)
      router.refresh()
    },
  })

  const handleCleanAll = async () => {
    try {
      await cleanAllMutation.mutateAsync()
    } catch {
      // withQueryHandling already shows the user-facing error toast.
    }
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 py-4 sm:p-6">
      <div className="flex flex-col items-stretch gap-3 px-4 sm:flex-row sm:items-center sm:justify-between sm:px-0">
        <div>
          <h1 className="text-2xl font-semibold text-foreground"><span key={tUi("knowledgeBase")}>{tUi("knowledgeBase")}</span></h1>
          <p className="text-gray-600 mt-1">
            <span key={tUi("manageDocumentsThatProvideContextToAIFor")}>{tUi("manageDocumentsThatProvideContextToAIFor")}</span></p>
        </div>
        <Button
          variant="outline"
          className="border-destructive/40 bg-background text-destructive hover:bg-destructive/10 hover:text-destructive sm:w-auto"
          onClick={() => setIsCleanAllOpen(true)}
          disabled={cleanAllMutation.isPending}
        >
          {cleanAllMutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="mr-2 h-4 w-4" />
          )}
          <span><span key={tUi("cleanAll")}>{tUi("cleanAll")}</span></span>
        </Button>
      </div>

      <Card className="rounded-none border-x-0 sm:rounded-lg sm:border-x">
        <CardContent className="p-4 pt-4 sm:p-6">
          <KnowledgeBaseDocumentsPage key={documentsRefreshKey} refreshVersion={sourceRefreshVersion}
            headerAction={<KnowledgeSourcesPanel presentation="connect-button" onKnowledgeChanged={() => setSourceRefreshVersion(version => version + 1)} />} />
        </CardContent>
      </Card>

      <AlertDialog open={isCleanAllOpen} onOpenChange={setIsCleanAllOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle><span key={tUi("deleteAllKnowledgeBaseDocuments")}>{tUi("deleteAllKnowledgeBaseDocuments")}</span></AlertDialogTitle>
            <AlertDialogDescription>
              <span key={tUi("thisDeletesEveryKnowledgeBaseDocumentForThe")}>{tUi("thisDeletesEveryKnowledgeBaseDocumentForThe")}</span></AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cleanAllMutation.isPending}><span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault()
                void handleCleanAll()
              }}
              disabled={cleanAllMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {cleanAllMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <span><span key={tUi("deleteEverything")}>{tUi("deleteEverything")}</span></span>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
